/**
 * SignalDetailDrawer — full enriched view of one signal.
 *
 * Header KPIs (dp/doi/score/timeframe) + 6 sections that mirror the
 * enricher pipeline output:
 *
 *   Volume / liquidity   — rvol, volume_oi_ratio, spread, orderbook imbalance
 *   CVD                  — cvd_1h, divergence label + strength
 *   Funding / trend      — funding rate, htf trend_1h / trend_4h
 *   Candle structure     — body / wicks
 *   BTC context          — market phase, 1h change, dumping/pumping flags
 *   ML                   — entry score, direction, direction score
 *
 * "Open decision trace" button switches to SignalDecisionDrawer for the
 * same signal — keeps the two views as independent surfaces but linked.
 *
 * After PULSE-40 every signal_type (pump_dump / divergence / liquidation /
 * reversal / super_pump_dump) populates the same scalar set, so the
 * drawer looks identical regardless of which detector fired.
 */
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/Button'
import { Drawer } from '@/components/ui/Drawer'
import {
  type SignalDetail,
  useSignalDetail,
} from '@/hooks/useDashboardApi'
import { cn } from '@/lib/cn'
import { formatNumber, formatPct, formatUsd } from '@/lib/format'

interface SignalDetailDrawerProps {
  signalId: number | null
  onClose: () => void
  /** Switch from detail → decision-trace drawer for the same signal. */
  onOpenDecision?: () => void
}

export function SignalDetailDrawer({
  signalId, onClose, onOpenDecision,
}: SignalDetailDrawerProps) {
  const { t } = useTranslation('app')
  const { data, isLoading, error } = useSignalDetail(signalId)

  const title = data
    ? `${data.symbol} · ${t(`signal_type.${data.signal_type}`, {
        defaultValue: data.signal_type,
      })}`
    : t('signal_detail.title')

  const subtitle = data
    ? `#${data.id} · ${data.timeframe ?? ''} · ${new Date(data.ts).toLocaleString()}`
    : signalId
    ? `#${signalId}`
    : undefined

  return (
    <Drawer
      open={signalId != null}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      widthClassName="w-[560px]"
    >
      {isLoading ? (
        <div className="px-5 py-10 text-center text-textFaint text-sm">—</div>
      ) : error || !data ? (
        <div className="px-5 py-10 text-center text-textFaint text-sm">
          {t('signal_detail.error')}
        </div>
      ) : (
        <div className="px-5 py-4 space-y-6 font-mono">
          <Headline detail={data} />
          {onOpenDecision && (
            <Button
              size="sm"
              variant="secondary"
              onClick={onOpenDecision}
              className="w-full"
            >
              {t('signal_detail.open_decision')}
            </Button>
          )}
          <VolumeSection detail={data} />
          <CvdSection detail={data} />
          <FundingTrendSection detail={data} />
          <CandleSection detail={data} />
          <BtcSection detail={data} />
          <MlSection detail={data} />
        </div>
      )}
    </Drawer>
  )
}

/* ─── Headline ──────────────────────────────────────────────────── */

function Headline({ detail }: { detail: SignalDetail }) {
  const { t } = useTranslation('app')
  const dirTone = detail.direction === 'long'
    ? 'text-pulse'
    : detail.direction === 'short' ? 'text-rose' : 'text-textDim'
  return (
    <>
      <div className="flex items-center gap-2">
        <span className={cn(
          'pill text-sm',
          detail.direction === 'long' ? 'bg-pulse-faint text-pulse'
            : detail.direction === 'short' ? 'bg-rose-faint text-rose'
            : 'bg-elevated text-textDim',
        )}>
          {t(`dashboard.open_positions.side.${detail.direction}`, {
            defaultValue: detail.direction,
          })}
        </span>
        <span className={cn('text-sm', dirTone)}>
          {detail.score != null ? `score ${detail.score.toFixed(1)}` : ''}
        </span>
        {detail.matched_for_me && (
          <span className="ml-auto pill bg-fire-faint text-fire">
            {t('dashboard.signals_feed.matched')}
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <KpiBox
          label={t('signal_detail.dp')}
          value={detail.dp_pct != null
            ? formatPct(detail.dp_pct, { sign: true }) : '—'}
          tone={pnlToneFromValue(detail.dp_pct)}
        />
        <KpiBox
          label={t('signal_detail.doi')}
          value={detail.doi_pct != null
            ? formatPct(detail.doi_pct, { sign: true }) : '—'}
          tone={pnlToneFromValue(detail.doi_pct)}
        />
        <KpiBox
          label={t('signal_detail.doi_usd')}
          value={detail.doi_abs_usd != null
            ? formatUsd(detail.doi_abs_usd, { compact: true }) : '—'}
        />
        <KpiBox
          label={t('signal_detail.price')}
          value={detail.current_price != null
            ? formatNumber(detail.current_price, 4) : '—'}
        />
      </div>
    </>
  )
}

/* ─── Sections ──────────────────────────────────────────────────── */

function VolumeSection({ detail }: { detail: SignalDetail }) {
  const { t } = useTranslation('app')
  if (!hasAny([
    detail.rvol, detail.rvol_5m, detail.rvol_15m,
    detail.volume_oi_ratio, detail.spread_pct, detail.orderbook_imbalance,
    detail.oi_current,
  ])) return null
  return (
    <Section title={t('signal_detail.volume_section')}>
      <Row label="rvol" value={fmt(detail.rvol, 2)} />
      <Row label="rvol 5m / 15m"
        value={`${fmt(detail.rvol_5m, 2)} / ${fmt(detail.rvol_15m, 2)}`} />
      <Row label="vol/OI" value={fmt(detail.volume_oi_ratio, 3)} />
      <Row label={t('signal_detail.spread')}
        value={detail.spread_pct != null ? formatPct(detail.spread_pct, { digits: 3 }) : '—'} />
      <Row label={t('signal_detail.ob_imbalance')}
        value={detail.orderbook_imbalance != null
          ? formatPct(detail.orderbook_imbalance * 100, { digits: 1, sign: true }) : '—'}
        valueClass={detail.orderbook_imbalance == null ? 'text-textFaint'
          : detail.orderbook_imbalance >= 0 ? 'text-pulse' : 'text-rose'} />
      <Row label="OI"
        value={detail.oi_current != null
          ? formatUsd(detail.oi_current, { compact: true }) : '—'} />
    </Section>
  )
}

function CvdSection({ detail }: { detail: SignalDetail }) {
  const { t } = useTranslation('app')
  if (!hasAny([detail.cvd_1h, detail.cvd_divergence, detail.cvd_divergence_strength])) return null
  return (
    <Section title={t('signal_detail.cvd_section')}>
      <Row label="cvd 1h"
        value={detail.cvd_1h != null ? formatUsd(detail.cvd_1h, { sign: true, compact: true }) : '—'}
        valueClass={detail.cvd_1h == null ? 'text-textFaint'
          : detail.cvd_1h >= 0 ? 'text-pulse' : 'text-rose'} />
      <Row label={t('signal_detail.cvd_alignment')}
        value={detail.cvd_divergence ?? '—'} />
      <Row label={t('signal_detail.cvd_strength')}
        value={fmt(detail.cvd_divergence_strength, 3)} />
    </Section>
  )
}

function FundingTrendSection({ detail }: { detail: SignalDetail }) {
  const { t } = useTranslation('app')
  if (!hasAny([detail.funding_rate, detail.trend_1h, detail.trend_4h,
               detail.htf_direction, detail.funding_bias])) return null
  return (
    <Section title={t('signal_detail.funding_trend_section')}>
      <Row label={t('signal_detail.funding_rate')}
        value={detail.funding_rate != null
          ? formatPct(detail.funding_rate * 100, { digits: 4, sign: true }) : '—'}
        valueClass={detail.funding_rate == null ? 'text-textFaint'
          : detail.funding_rate >= 0 ? 'text-pulse' : 'text-rose'} />
      <Row label={t('signal_detail.funding_bias')}
        value={detail.funding_bias ?? '—'} />
      <Row label="trend 1h" value={detail.trend_1h ?? '—'} />
      <Row label="trend 4h" value={detail.trend_4h ?? '—'} />
      <Row label="HTF direction" value={detail.htf_direction ?? '—'} />
    </Section>
  )
}

function CandleSection({ detail }: { detail: SignalDetail }) {
  const { t } = useTranslation('app')
  if (!hasAny([detail.candle_body_pct, detail.upper_wick_pct, detail.lower_wick_pct])) return null
  return (
    <Section title={t('signal_detail.candle_section')}>
      <Row label={t('signal_detail.body')}
        value={detail.candle_body_pct != null
          ? formatPct(detail.candle_body_pct * 100, { digits: 1 }) : '—'} />
      <Row label={t('signal_detail.upper_wick')}
        value={detail.upper_wick_pct != null
          ? formatPct(detail.upper_wick_pct * 100, { digits: 1 }) : '—'} />
      <Row label={t('signal_detail.lower_wick')}
        value={detail.lower_wick_pct != null
          ? formatPct(detail.lower_wick_pct * 100, { digits: 1 }) : '—'} />
    </Section>
  )
}

function BtcSection({ detail }: { detail: SignalDetail }) {
  const { t } = useTranslation('app')
  if (!hasAny([detail.btc_market_phase, detail.btc_change_1h_pct,
               detail.btc_is_dumping, detail.btc_is_pumping])) return null
  const phaseLabel = detail.btc_market_phase
    ? t(`dashboard.market.phase.${detail.btc_market_phase}`, {
        defaultValue: detail.btc_market_phase,
      })
    : '—'
  return (
    <Section title={t('signal_detail.btc_section')}>
      <Row label={t('dashboard.market.btc_phase')} value={phaseLabel} />
      <Row label="BTC Δ 1h"
        value={detail.btc_change_1h_pct != null
          ? formatPct(detail.btc_change_1h_pct, { sign: true }) : '—'}
        valueClass={detail.btc_change_1h_pct == null ? 'text-textFaint'
          : detail.btc_change_1h_pct >= 0 ? 'text-pulse' : 'text-rose'} />
      {detail.btc_is_dumping && (
        <Row label="BTC dumping" value="✓" valueClass="text-rose" />
      )}
      {detail.btc_is_pumping && (
        <Row label="BTC pumping" value="✓" valueClass="text-pulse" />
      )}
    </Section>
  )
}

function MlSection({ detail }: { detail: SignalDetail }) {
  const { t } = useTranslation('app')
  if (!hasAny([detail.ml_score_entry, detail.ml_direction, detail.ml_direction_score])) return null
  return (
    <Section title={t('signal_detail.ml_section')}>
      <Row label={t('signal_detail.ml_score')}
        value={detail.ml_score_entry != null
          ? detail.ml_score_entry.toFixed(3) : '—'} />
      <Row label={t('signal_detail.ml_direction')}
        value={detail.ml_direction ?? '—'} />
      <Row label={t('signal_detail.ml_dir_score')}
        value={detail.ml_direction_score != null
          ? detail.ml_direction_score.toFixed(3) : '—'} />
    </Section>
  )
}

/* ─── building blocks ───────────────────────────────────────────── */

function KpiBox({
  label, value, tone,
}: {
  label: string
  value: string
  tone?: 'pulse' | 'rose'
}) {
  return (
    <div className="bg-ink p-3 border border-line">
      <div className="text-textFaint text-2xs uppercase tracking-wider">
        {label}
      </div>
      <div className={cn(
        'mt-1 text-sm tabular-nums',
        tone === 'pulse' ? 'text-pulse' : tone === 'rose' ? 'text-rose' : 'text-text',
      )}>
        {value}
      </div>
    </div>
  )
}

function Section({
  title, children,
}: {
  title: string
  children: React.ReactNode
}) {
  return (
    <section>
      <h3 className="text-textFaint text-xs uppercase tracking-wider mb-2">
        {title}
      </h3>
      <div className="divide-y divide-line/40 border border-line bg-ink">
        {children}
      </div>
    </section>
  )
}

function Row({
  label, value, valueClass,
}: {
  label: string
  value: string
  valueClass?: string
}) {
  return (
    <div className="flex items-center px-3 py-2 text-xs">
      <span className="text-textDim">{label}</span>
      <span className={cn('ml-auto tabular-nums', valueClass ?? 'text-text')}>
        {value}
      </span>
    </div>
  )
}

/* ─── helpers ───────────────────────────────────────────────────── */

function fmt(v: number | null, digits = 2): string {
  return v != null ? formatNumber(v, digits) : '—'
}

function pnlToneFromValue(v: number | null): 'pulse' | 'rose' | undefined {
  if (v == null) return undefined
  return v >= 0 ? 'pulse' : 'rose'
}

/** True if at least one of the values is non-null. Used to hide empty sections. */
function hasAny(values: Array<number | string | boolean | null | undefined>): boolean {
  return values.some((v) => v != null && v !== '')
}
